/* eslint-disable no-param-reassign */
import { createWithEqualityFn as create } from "zustand/traditional"
import { produce } from "immer"

// Chip rules (`?rules=`) deliberately don't live here — they're URL-derived
// page state for the Guests/TableView and shouldn't leak into the cross-page
// guest store. Consumers read them from useSearchParams via parseFiltersFromUrl.
type Store = {
  searchString: string,
  groupId: string,
  segmentId?: string,
  order: string,
  allSelected: boolean,
  selectAllBlocked: boolean,
  selectedGuestIds: string[],
  setSelectAllBlocked: (blocked: boolean) => void;
  selectAll: () => void;
  deselectAll: () => void;
  addSelectedGuestId:(guestId: string) => void,
  removeSelectedGuestId: (guestId: string) => void,
  filter: (
    { groupId, segmentId, searchString }: {
      groupId?: string, segmentId?: string, searchString?: string,
    }
  ) => void;
  sort: (order) => void;
  reset: () => void;
}

const immer = (config) => (set, get, api) => config((partial, replace) => {
  const nextState = typeof partial === "function"
    ? produce(partial)
    : partial

  return set(nextState, replace)
}, get, api)

const initialState = {
  searchString: "",
  groupId: "",
  segmentId: undefined,
  order: "first_name_asc",
  allSelected: false,
  selectAllBlocked: false,
  selectedGuestIds: [],
}

export default create<Store>(immer((set) => ({
  ...initialState,
  addSelectedGuestId: (guestId) => set(({ selectedGuestIds: ids }) => {
    ids.push(guestId)
  }),
  removeSelectedGuestId: (guestId) => set(({ selectedGuestIds: ids }) => {
    const index = ids.indexOf(guestId)
    if (index !== -1) ids.splice(index, 1)
  }),
  setSelectAllBlocked: (blocked) => set((s) => {
    s.selectAllBlocked = blocked
    if (blocked) s.allSelected = false
  }),
  selectAll: () => set((s) => {
    if (s.selectAllBlocked) return
    s.searchString = ""
    s.selectedGuestIds.splice(0, s.selectedGuestIds.length)
    s.allSelected = true
  }),
  deselectAll: () => set((s) => {
    s.selectedGuestIds.splice(0, s.selectedGuestIds.length)
    s.allSelected = false
  }),
  filter: ({ groupId, segmentId, searchString = "" }) => set((s) => {
    s.searchString = searchString
    s.groupId = groupId
    s.segmentId = segmentId
  }),
  sort: (order) => set((s) => {
    s.order = order
  }),
  reset: () => set((s) => {
    s.searchString = ""
    s.groupId = ""
    s.segmentId = undefined
    s.allSelected = false
    s.selectAllBlocked = false
    s.selectedGuestIds = []
  }),
})))
